import React, { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import FolderIcon from "@mui/icons-material/FolderOpenOutlined"
import AssignmentIcon from "@mui/icons-material/AssignmentOutlined"
import SearchIcon from "@mui/icons-material/SearchOutlined"
import WorkspaceIcon from "@mui/icons-material/WorkspacePremiumOutlined"
import LayersIcon from "@mui/icons-material/LayersOutlined"
import PropTypes from "prop-types"
import i18n from "i18n"
import PartDetails from "./detallesPartida/partDetails"
import { useBudget2023Context } from "context/budget2023/Budget2023Context"
import { Stack, Button, CircularProgress } from "@mui/material"
import validateFields from "./validateFields"
import FileIndex from "./fileHandler/fileIndex"
import { useBankPricesContext } from "context/bankPrices/bankPricesContext"
import PriceSearcher from "./priceSearch"
import { calcularImporteDeDescomposicion, getCurrency } from "utils/functions"
import { useAuthContext } from "context/auth/authContext"
import { isNil } from "lodash"
import { API, Auth } from "aws-amplify"
import DecompositionTypeEnum from "common/DecompositionTypeEnum"
import Tooltip from "@mui/material/Tooltip"
import { useWrapperModals } from "context/wrapperModals/WrapperModalsContext"
import { useModalPartida } from "hooks/modalPartida/useModalPartida"
import RightFooterGrid from "./RightFooterGrid"

import { useDecimalFormatter } from "hooks/useDecimalFormatter"
import {
  UNITS,
  MEASUREMENTS,
  MARGINS,
  TOTAL_DECOMPOSITIONS,
} from "constants/decimals"

const decompositionTypeMap = {
  0: "coste",
  1: "manodeobra",
  2: "maquinaria",
  3: "material",
  4: "residuoAdicional",
  5: "residuoClasificacion",
}

const RightGridContent = ({
  bancoDePrecios,
  partidaState,
  details,
  setDetails,
  handleClick,
  setErrorMessage,
  setPartidaState,
  imInUpdate,
  setOpen,
  referenciaObject,
  srcPage,
  rightFooterRef,
}) => {
  const { roundToDecimals, getUnitWithMargin } = useDecimalFormatter()

  const locationPath = window.location.pathname.split("/")[1]
  const isBankPricePath = locationPath === "bancodeprecios"
  const isExtrasPath = locationPath === "extras"
  const isAdjusted = partidaState.adjustedPartIds?.length > 0

  const contents =
    bancoDePrecios == "BANCO DE PRECIOS REFERENCIA"
      ? ["Details"]
      : ["Details", "Files", "Layers", "Certificates", "PartSearcher"]
  const [content, setContent] = useState("Details")
  const {
    createPartidaV2,
    updatePartidav2,
    isContradictingPart,
    setIsContradictingPart,
    isBudgetClosed,
    marginModal,
    budgetMode,
  } = useBudget2023Context()
  const [loading, setLoading] = useState(false)
  const { createPartBankTree, updatePartPrice, getPartsLenghtByChapterId } =
    useBankPricesContext()
  const { user } = useAuthContext()
  const { newModalPart, bc3Link } = useWrapperModals()

  const { state, loadInitialRightFooterData } = useModalPartida()

  useEffect(() => {
    loadInitialRightFooterData(partidaState, marginModal.formula)
  }, [])

  const SERVICE_CORE_BASE = "service-core-base"

  const isTender = budgetMode === "tender"

  const transformTypeToNumber = type => {
    return (
      Object.keys(decompositionTypeMap).find(
        key => decompositionTypeMap[key] === type,
      ) || 3
    )
  }

  const handleContradictingPart = async () => {
    try {
      setIsContradictingPart(false)
      const { partidaId, ...partidaStateForBody } = partidaState
      partidaStateForBody.decomposition = details.map(item => {
        return {
          ...item,
          price: roundToDecimals(item.price, UNITS),
          quantity: roundToDecimals(item.quantity, MEASUREMENTS),
          type: item.type
            ? DecompositionTypeEnum[item.type]
            : DecompositionTypeEnum.material,
        }
      })
      const newBody = {
        ...partidaStateForBody,
        quantity: roundToDecimals(state.leftGridContent.quantity, MEASUREMENTS),
        margin: roundToDecimals(state.rightFooter.marginPercent, MARGINS),
        price: roundToDecimals(state.leftGridContent.cost, UNITS),
        priceReduced: roundToDecimals(
          state.leftGridContent.priceReduced,
          UNITS,
        ),
        clientPrice: roundToDecimals(
          state.rightFooter.unitPriceWithDiscount,
          UNITS,
        ),
        clientPriceWithoutDiscount: roundToDecimals(
          state.rightFooter.unitPrice,
          UNITS,
        ),
      }

      let payload = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`,
          "Access-Control-Allow-Origin": "*",
        },
        body: newBody,
      }
      const resp = await API.post(
        SERVICE_CORE_BASE,
        `/extras/contradict/${partidaId}`,
        payload,
      )
      setOpen(false)
      window.location.href = `/extras/${partidaState.projectId}`
    } catch (error) {
      console.log("🚀 error, newModalPart", error)
      setOpen(false)
    }
  }

  const handleCreatePartida = async () => {
    setLoading(true)
    if (validateFields(partidaState, referenciaObject, setErrorMessage)) {
      if (!bancoDePrecios) {
        await createPartidaV2(
          partidaState.accountId,
          partidaState.chapterId,
          partidaState.projectId,
          "last",
          undefined,
          {
            ...partidaState,
            decomposition: details,
            quantity: roundToDecimals(
              state.leftGridContent.quantity,
              MEASUREMENTS,
            ),
            margin: roundToDecimals(state.rightFooter.marginPercent, MARGINS),
            price: roundToDecimals(state.leftGridContent.cost, UNITS),
            priceReduced: roundToDecimals(
              state.leftGridContent.priceReduced,
              UNITS,
            ),
            costReduction: roundToDecimals(
              state.leftGridContent.costReduction,
              UNITS,
            ),
            clientPrice: roundToDecimals(
              state.rightFooter.unitPriceWithDiscount,
              UNITS,
            ),
            clientPriceWithoutDiscount: roundToDecimals(
              state.rightFooter.unitPrice,
              UNITS,
            ),
            discount: roundToDecimals(
              state.rightFooter.discountPercentage,
              MARGINS,
            ),
          },
          newModalPart.from,
        )
      } else {
        const position = await getPartsLenghtByChapterId(partidaState.chapterId)
        await createPartBankTree({
          ...partidaState,
          decomposition: details,
          chapterBankId: partidaState.chapterId,
          userPrice: roundToDecimals(state.leftGridContent.cost, UNITS),
          quantity: roundToDecimals(
            state.leftGridContent.quantity,
            MEASUREMENTS,
          ),
          price: roundToDecimals(state.leftGridContent.cost, UNITS),
          position: position,
        })
        setDetails([])
      }
      setLoading(false)
      setOpen(false)
    } else {
      handleClick()
      setLoading(false)
    }
    bc3Link.setProperty({
      open: false,
      data: null,
      chapterId: null,
    })
  }

  const handleUpdatePartida = async () => {
    setLoading(true)
    if (!bancoDePrecios) {
      await updatePartidav2(
        {
          ...partidaState,
          decomposition: details,
          costeIndirecto: 0,
          industrialProfit: 0,
          quantity: roundToDecimals(
            state.leftGridContent.quantity,
            MEASUREMENTS,
          ),
          margin: roundToDecimals(state.rightFooter.marginPercent, MARGINS),
          price: roundToDecimals(state.leftGridContent.cost, UNITS),
          discount: state.rightFooter.discountPercentage,
          priceReduced: roundToDecimals(
            state.leftGridContent.priceReduced,
            UNITS,
          ),
          costReduction: roundToDecimals(
            state.leftGridContent.costReduction,
            UNITS,
          ),
          clientPriceWithoutDiscount: roundToDecimals(
            state.rightFooter.unitPrice,
            UNITS,
          ),
          clientPrice: roundToDecimals(
            state.rightFooter.unitPriceWithDiscount,
            UNITS,
          ),
        },
        partidaState.chapterId,
        partidaState.accountId,
        false,
        newModalPart.from,
      )
    } else {
      await updatePartPrice(
        {
          ...partidaState,
          decomposition: details,
          costeIndirecto: 0,
          industrialProfit: 0,
          userPrice: roundToDecimals(state.leftGridContent.cost, UNITS),
          quantity: roundToDecimals(
            state.leftGridContent.quantity,
            MEASUREMENTS,
          ),
          price: roundToDecimals(state.leftGridContent.cost, UNITS),
          position: 0,
          clientPrice: getUnitWithMargin(
            state.rightFooter.unitPrice,
            -partidaState.discount,
          ),
        },
        partidaState.chapterId,
        partidaState.accountId,
        false,
      )
    }

    setLoading(false)
    setOpen(false)
    bc3Link.setProperty({
      open: false,
      data: null,
      chapterId: null,
    })
    setDetails([])
  }

  const currencySymbol = getCurrency(user["custom:currency"])

  useEffect(() => {
    if (imInUpdate || !isNil(bc3Link.data) || !isNil(newModalPart.partida)) {
      if (partidaState.decomposition) {
        let aux = partidaState.decomposition
        for (let i = 0; i < partidaState.decomposition.length; i++) {
          const totalAux = roundToDecimals(
            calcularImporteDeDescomposicion(partidaState.decomposition[i]),
            TOTAL_DECOMPOSITIONS,
          )
          aux[i].total = totalAux

          aux[i].price = partidaState.decomposition[i].price
          aux[i].quantity = partidaState.decomposition[i].quantity
          aux[i].unity = partidaState.decomposition[i].unity.toString()
          aux[i].description =
            partidaState.decomposition[i].description.toString()

          let typeEnumStr
          switch (partidaState.decomposition[i].type) {
            case 0:
              typeEnumStr = "coste"
              break
            case 1:
              typeEnumStr = "manodeobra"
              break
            case 2:
              typeEnumStr = "maquinaria"
              break
            case 3:
              typeEnumStr = "material"
              break
            case 4:
              typeEnumStr = "residuoAdicional"
              break
            case 5:
              typeEnumStr = "residuoClasificacion"
              break
          }

          aux[i].type = typeEnumStr
            ? typeEnumStr
            : partidaState.decomposition[i].type
        }
        setDetails(aux)
      }
    } else {
      // no-op
    }
  }, [partidaState.decomposition])

  const getSvg = cont => {
    if (cont == "Details") {
      return (
        <>
          <AssignmentIcon />
          <a>Detalle partida</a>
        </>
      )
    } else if (cont == "Files") {
      return (
        <>
          <FolderIcon />
          <a>Archivos adjuntos</a>
        </>
      )
    } else if (cont == "Layers") {
      return (
        <>
          <LayersIcon />
          <a>Pliegos</a>
        </>
      )
    } else if (cont == "Certificates") {
      return (
        <>
          <WorkspaceIcon />
          <a>Certificados</a>
        </>
      )
    }
    if (cont == "PartSearcher" && !isTender) {
      return (
        <>
          <SearchIcon />
          <a>Buscar precio</a>
        </>
      )
    }
  }

  const saveToolTip = () => {
    if (isBankPricePath || isContradictingPart || isExtrasPath) {
      return ""
    }

    if (
      isBudgetClosed &&
      srcPage !== "extras and contradictions" &&
      srcPage !== "priceBanks"
    ) {
      return i18n.t("validation.editNotAllowedProjectClosed")
    }

    if (Number(partidaState?.cert_previous ?? 0) > 0) {
      return i18n.t("validation.certifiedItem")
    }

    if (isAdjusted) {
      return i18n.t("validation.cannotEditAdjustedPart")
    }

    return ""
  }

  const saveDisabled = () => {
    if (isContradictingPart) return false
    if (isExtrasPath) return false

    if (isAdjusted) return true

    if (
      !isBankPricePath &&
      isBudgetClosed &&
      srcPage !== "extras and contradictions" &&
      srcPage !== "priceBanks"
    ) {
      return true
    }

    if (Number(partidaState?.cert_previous ?? 0) > 0) {
      return true
    }

    return false
  }

  return (
    <>
      <Stack direction="column" sx={{ height: "100%" }}>
        <div
          style={{
            width: "100%",
            height: "62px",
            backgroundColor: "#F6F7FB",
            borderTopRightRadius: "10px",
            display: "flex",
            alignItems: "center",
            paddingLeft: "16px",
            paddingRight: "20px",
          }}
        >
          <ul
            style={{
              display: "flex",
              listStyle: "none",
              marginBottom: "0",
              gap: "18px",
              padding: "0",
              height: "100%",
            }}
          >
            {contents.map(
              (c, index) =>
                c !== "Layers" &&
                c !== "Certificates" &&
                (imInUpdate || c !== "PartSearcher") && (
                  <li
                    key={index}
                    style={{
                      display: "flex",
                      gap: "6px",
                      fontWeight: "500",
                      cursor: "pointer",
                      height: "92%",
                      color: c == content ? "#1976D2" : "#414141",
                      borderBottom: c == content ? "2px solid #1976D2" : "none",
                      alignItems: "center",
                    }}
                    onClick={() => {
                      setContent(c)
                    }}
                  >
                    {getSvg(c)}
                  </li>
                ),
            )}
          </ul>
          {loading ? (
            <CircularProgress
              size={24}
              style={{ marginLeft: "auto", marginRight: "0px" }}
            />
          ) : bancoDePrecios == "BANCO DE PRECIOS REFERENCIA" ? null : (
            <Tooltip title={saveToolTip()}>
              <div style={{ marginLeft: "auto", marginRight: "0px" }}>
                <Button
                  color="branded"
                  variant="contained"
                  disabled={saveDisabled()}
                  onClick={() => {
                    isContradictingPart
                      ? handleContradictingPart()
                      : imInUpdate
                        ? handleUpdatePartida()
                        : handleCreatePartida()
                  }}
                >
                  {i18n.t("Guardar")}
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
        <div style={{ height: "100%", overflowY: "auto" }}>
          {content == "Details" ? (
            <PartDetails
              bancoDePrecios={bancoDePrecios}
              partidaState={partidaState}
              details={details}
              setDetails={setDetails}
              isTender={isTender}
              showDynamicElementButton
            />
          ) : content == "Files" ? (
            <FileIndex
              imInUpdate={imInUpdate}
              partidaState={partidaState}
              setPartidaState={setPartidaState}
            />
          ) : content == "PartSearcher" && imInUpdate ? (
            <PriceSearcher
              partidaState={partidaState}
              setPartidaState={setPartidaState}
              setDetails={setDetails}
              details={details}
              setContent={setContent}
            />
          ) : null}
        </div>
      </Stack>
      {rightFooterRef?.current &&
        createPortal(
          <RightFooterGrid
            currency={currencySymbol}
            bancoDePrecios={bancoDePrecios}
          />,
          rightFooterRef.current,
        )}
    </>
  )
}

RightGridContent.propTypes = {
  partidaState: PropTypes.object,
  setPartidaState: PropTypes.func,
  imInUpdate: PropTypes.bool,
  setOpen: PropTypes.func,
  referenciaObject: PropTypes.object,
  handleClick: PropTypes.func,
  setErrorMessage: PropTypes.func,
  details: PropTypes.array,
  setDetails: PropTypes.func,
  bancoDePrecios: PropTypes.string,
  srcPage: PropTypes.string,
  rightFooterRef: PropTypes.object,
}

export default RightGridContent
